import React, { useEffect, useState } from 'react';
import styles from './ApprovalQMonthSection.module.scss';
import { useAppSelector } from '../../../../../redux/hooks';
import { RootState } from '../../../../../redux/store';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../common/Icon/Icon';
import Button from '../../../../common/Button/Button';
import { useLazyGetUserNameWithIDQuery } from '../../../../../redux/services/hatvAboStatisticAPI';
import { isEmpty } from 'lodash';
import { getFullMonthYear, useProfileParams } from '../../../../../utils/common';
import { useNavigate } from 'react-router-dom';
import Modal from '../../../../common/Modal/Modal';
import HATVQualificationActionModal, {
  IHATVQualificationActionProps,
} from '../../../HATVQualificationActionModal/HATVQualificationActionModal';
import { HATV_Q_MONTH_APPROVAL_STATUS } from '../../../../../constants/dataConstants';
import { saveStateToSessionStorage } from '../../../../../utils/sessionStorageUtility';
import { toTitleCase } from '../../../../../utils/TextFormatter';

interface Props {
  selectedMonth?: string | number | undefined;
  qmonthDetailsData?: any;
  setIsStatusUpdate?: (apiStatus: string) => void;
}

const ApprovalQMonthSection: React.FC<Props> = ({
  selectedMonth,
  qmonthDetailsData,
  setIsStatusUpdate,
}) => {
  const [selectedAction, setSelectedAction] = useState(false);
  const navigate = useNavigate();
  const currentTheme = useAppSelector((state: RootState) => state.theme.currentTheme);

  const [qMonthModalState, setQMonthModalState] = useState<Omit<
    IHATVQualificationActionProps,
    'onClose'
  > | null>(null);

  const { affiliateCode, aboNumber } = useProfileParams();
  const [isQMonthApproval, setIsAwardApproval] = useState(false);
  const monthYearText = getFullMonthYear(String(selectedMonth ?? ''));
  const { t } = useTranslation();
  const [tableUplineImpactData, setTableData] = useState<any>(qmonthDetailsData);
  const [userNameById, setUserNameById] = useState<string>('');
  const [getUserNameFromId] = useLazyGetUserNameWithIDQuery();

  useEffect(() => {
    setTableData(qmonthDetailsData);
    if (!isEmpty(qmonthDetailsData) && qmonthDetailsData[0]?.hatvAdmin?.userNativeId) {
      getUserNameFromId({
        nativeId: qmonthDetailsData[0]?.hatvAdmin?.userNativeId,
      }).then((userDetailsResp: any) => {
        setUserNameById(userDetailsResp?.data?.body?.fullName);
      });
    }
  }, [qmonthDetailsData]);

  const iconColor = !selectedAction
    ? currentTheme === 'light'
      ? '#2c2c2c'
      : '#D8D8D9'
    : currentTheme === 'light'
      ? '#5E5E5E'
      : '#000';

  const ViewApprovalModalStyles = {
    minWidth: '472px',
    minHeight: '400px',
    overflow: 'visible',
  };

  const decideColourForStatus = (status: string) => {
    switch (status) {
      case 'red':
        return currentTheme === 'light' ? '#D91734' : '#F16A81';
      case 'yellow':
        return currentTheme === 'light' ? '#DA7600' : '#FED773';
      case 'no violations':
        return currentTheme === 'light' ? '#5969D6' : '#99B2F5';
      default:
        return currentTheme === 'light' ? '#5969D6' : '#99B2F5';
    }
  };
  const handleInvestigateAI = () => {
    saveStateToSessionStorage({ affiliateCode, aboNumber });
    navigate('/hatv', {
      state: { showHatvChatbot: true, from: 'approvalqmonthsection' },
    });
  };

  const onModalClose = () => {
    setIsAwardApproval(false);
    setQMonthModalState(null);
  };

  const handleQualificationModalAction = (status: string) => {
    setIsStatusUpdate && setIsStatusUpdate(status);
    setIsAwardApproval(false);
    setQMonthModalState(null);
  };

  const approvalStatus =
    qmonthDetailsData[0]?.qMonthApproval?.approvalStatus ||
    qmonthDetailsData[0]?.qMonthApproval?.approvalStatus === 0
      ? t(`q_month_approval_status.${qmonthDetailsData[0]?.qMonthApproval?.approvalStatus}`)
      : '-';

  const approveHandler = () => {
    setIsAwardApproval(true);
    setQMonthModalState({
      aboNumber: aboNumber.toString(),
      affiliateCode,
      approvedFor: localStorage.getItem('subHeaderName') || '',
      approvalType: 1,
      qualificationStatus: +approvalStatus,
      closingMonth: selectedMonth ? +selectedMonth : undefined,
      currentStatus: qmonthDetailsData[0]?.qMonthApproval?.approvalStatus || 0,
    });
  };

  const denyHandler = () => {
    setIsAwardApproval(true);
    setQMonthModalState({
      aboNumber: aboNumber.toString(),
      affiliateCode,
      approvedFor: localStorage.getItem('subHeaderName') || '',
      approvalType: 9,
      qualificationStatus: +approvalStatus,
      closingMonth: selectedMonth ? +selectedMonth : undefined,
      currentStatus: qmonthDetailsData[0]?.qMonthApproval?.approvalStatus || 0,
    });
  };

  const onHoldHandler = () => {
    setIsAwardApproval(true);
    setQMonthModalState({
      aboNumber: aboNumber.toString(),
      affiliateCode,
      approvedFor: localStorage.getItem('subHeaderName') || '',
      approvalType: 2,
      qualificationStatus: +approvalStatus,
      closingMonth: selectedMonth ? +selectedMonth : undefined,
      currentStatus: qmonthDetailsData[0]?.qMonthApproval?.approvalStatus || 0,
    });
  };
  const handleClick = () => {
    navigate(`/assignment/${affiliateCode}/${aboNumber}/${2}`); // role 2 for Case Evaluator
  };
  const role = qmonthDetailsData?.[0]?.hatvApprovalRoleIndicators;
  const currentStatus: number | undefined = qmonthDetailsData?.[0]?.qMonthApproval?.approvalStatus;
  const canUpdate = !!role?.isAllowToUpdate;

  const isApproveDisabled = !canUpdate || currentStatus === HATV_Q_MONTH_APPROVAL_STATUS.APPROVE;
  const isDenyDisabled = !canUpdate || currentStatus === HATV_Q_MONTH_APPROVAL_STATUS.DENY;
  const isHoldDisabled = !canUpdate || currentStatus === HATV_Q_MONTH_APPROVAL_STATUS.ON_HOLD;

  return (
    <div className={`${styles.hatv_Qmonth_status}`}>
      <div className={styles.header_top}>
        <div className={styles.status_content_wrapper}>
          <div className={styles.status_wrapper}>
            <div className={styles.status_section}>
              <span className={`status_label ${styles.status_label}`}>
                {`${monthYearText} ${t('hatv_status')}`}
              </span>
              <div className={`flag_wrapper ${styles.flag_wrapper}`}>
                {qmonthDetailsData[0]?.hatvKpis?.summary?.kpiFlagName ? (
                  <Icon
                    name="flag"
                    size={14}
                    color={
                      qmonthDetailsData[0]?.hatvKpis?.summary?.kpiFlagName?.toLocaleLowerCase() ===
                      'red'
                        ? currentTheme === 'light'
                          ? '#D91734'
                          : '#F16A81'
                        : currentTheme === 'light'
                          ? '#DA7600'
                          : '#FED773'
                    }
                    className={styles.flag}
                  />
                ) : (
                  ''
                )}
                <span
                  className={`status_badge ${styles.status_badge}`}
                  style={{
                    color: decideColourForStatus(
                      qmonthDetailsData[0]?.hatvKpis?.summary?.kpiFlagName?.toLocaleLowerCase() ||
                        'no violations'
                    ),
                  }}
                >
                  {`${qmonthDetailsData[0]?.hatvKpis?.summary?.kpiFlagName ? (qmonthDetailsData[0]?.hatvKpis?.summary?.kpiFlagName?.toLocaleLowerCase() === 'red' ? `${toTitleCase(qmonthDetailsData[0]?.hatvKpis?.summary?.kpiFlagName)} ${approvalStatus}` : toTitleCase(qmonthDetailsData[0]?.hatvKpis?.summary?.kpiFlagName)) : t('No_violation')}`}
                </span>
              </div>
            </div>
            <div className={`sub_header ${styles.sub_header}`}>
              <span className="section_sub_heading">
                {(t('CASE_EVALUATOR') || '')?.toUpperCase()}
              </span>
              <span className={` case_text ${styles.caseLabel}`}>
                {userNameById ? (
                  <>
                    {userNameById}{' '}
                    <span className={`assign_name ${styles.assign_name}`} onClick={handleClick}>
                      {t('Change')}
                    </span>
                  </>
                ) : (
                  <span className={`assign_name ${styles.assign_name}`} onClick={handleClick}>
                    {' '}
                    {t('assign_case_evaluator')}
                  </span>
                )}
              </span>
            </div>
          </div>
          <div className={`status_action_wrapper ${styles.status_action_wrapper}`}>
            {qmonthDetailsData[0]?.hatvApprovalRoleIndicators?.isAllowToUpdate && (
              <>
                <div className={styles.action_btn}>
                  <Button
                    id="approvalbtn"
                    onClick={approveHandler}
                    label={t('Approve')}
                    style="unfilled"
                    isIcon={true}
                    iconName="tick_icon"
                    iconSize={16}
                    themeColor={iconColor}
                    className={`btn ${styles.btn}`}
                    isDisabled={isApproveDisabled}
                    disabledThemeColor={'var(--text-text-disabled)'}
                    customStyle={
                      isApproveDisabled
                        ? {
                            border: '2px solid var(--text-text-disabled)',
                            backgroundColor: 'inherit',
                          }
                        : undefined
                    }
                  />
                </div>

                <div className={styles.action_btn}>
                  <Button
                    id="denybtn"
                    onClick={denyHandler}
                    label={t('Deny')}
                    style="unfilled"
                    isIcon={true}
                    iconName="cross_circle_icon"
                    iconSize={16}
                    themeColor={iconColor}
                    className={`btn ${styles.btn}`}
                    isDisabled={isDenyDisabled}
                    disabledThemeColor={'var(--text-text-disabled)'}
                    customStyle={
                      isDenyDisabled
                        ? {
                            border: '2px solid var(--text-text-disabled)',
                            backgroundColor: 'inherit',
                          }
                        : undefined
                    }
                  />
                </div>
                <div className={styles.action_btn}>
                  <Button
                    id="routingbtn"
                    onClick={onHoldHandler}
                    label={t('On_Hold')}
                    style="unfilled"
                    isIcon={true}
                    iconName="pause_icon"
                    iconSize={16}
                    themeColor={iconColor}
                    className={`btn ${styles.btn}`}
                    isDisabled={isHoldDisabled}
                    disabledThemeColor={'var(--text-text-disabled)'}
                    customStyle={
                      isHoldDisabled
                        ? {
                            border: '2px solid var(--text-text-disabled)',
                            backgroundColor: 'inherit',
                          }
                        : undefined
                    }
                  />
                </div>
              </>
            )}

            <div className={styles.action_btn} onClick={handleInvestigateAI}>
              <div className={`btn ${styles.ai_title_wrapper}`}>
                <Icon
                  name="ai_flower_logo"
                  size={21}
                  color={currentTheme === 'light' ? '#1176A9' : '#B2F9FF'}
                  className={styles.ai_flower_logo}
                />
                <span className={`ai_investigate_title ${styles.ai_title}`}>
                  {t('investigate_with_aI')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isQMonthApproval && (
        <Modal
          showDrawer={!!qMonthModalState}
          onClose={onModalClose}
          customStyle={ViewApprovalModalStyles}
        >
          {qMonthModalState && (
            <HATVQualificationActionModal
              {...qMonthModalState}
              onClose={onModalClose}
              setIsStatusUpdate={handleQualificationModalAction}
            />
          )}
        </Modal>
      )}
    </div>
  );
};

export default ApprovalQMonthSection;
